import { Box, Typography, Divider, useTheme } from "@mui/material";
import {
  CalendarToday,
  AccessTime,
  LocationOn,
  Phone,
  Person,
} from "@mui/icons-material";
import { tokens } from "../../../../theme";
import FuneralPreviewCard from "./FuneralPreviewCard";

interface QuotePreviewProps {
  belovedName: string;
  belovedPhoto?: string;
  eventDate?: string;
  eventTime?: string;
  location?: string;
  clientName: string;
  clientPhone: string;
  photoPreview?: string | null;
  birthDate?: string;
  deathDate?: string;
  timeZone?: string;
}

const QuotePreview = ({
  belovedName,
  eventDate,
  eventTime,
  location,
  clientName,
  clientPhone,
  photoPreview,
}: QuotePreviewProps) => {
 
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const hasBelovedInfo =
    (belovedName && belovedName !== "Beloved Name") || photoPreview;
  const hasEventInfo = eventDate || eventTime || location;
  const hasClientInfo = clientName || clientPhone;

  // Beloved Section
  const BelovedSection = () => (
    <Box sx={{ mb: 2.5 }}>
      <Typography
        variant="caption"
        sx={{
          color: "text.secondary",
          textTransform: "uppercase",
          letterSpacing: 0.5,
          fontWeight: 600,
        }}
      >
        Beloved Information
      </Typography>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mt: 1 }}>
        <Box
          sx={{
            width: 48,
            height: 48,
            borderRadius: "50%",
            border: "3px solid rgba(255,255,255,0.7)",
            backgroundColor: "#614338",
            backgroundImage: photoPreview ? `url(${photoPreview})` : "none",
            backgroundSize: "cover",
            backgroundPosition: "center",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {!photoPreview && (
            <Person
              sx={{
                fontSize: 46,
                color: "rgba(255,255,255,0.7)",
              }}
            />
          )}
        </Box>
        <Box>
          <Typography variant="body1" fontWeight={600}>
            {belovedName || "Not provided"}
          </Typography>
        </Box>
      </Box>
    </Box>
  );

  const EventSection = () => (
    <Box sx={{ mb: 2.5 }}>
      <Typography
        variant="caption"
        sx={{
          color: "text.secondary",
          textTransform: "uppercase",
          letterSpacing: 0.5,
          fontWeight: 600,
        }}
      >
        Event Details
      </Typography>
      <Box sx={{ mt: 1.5, display: "flex", flexDirection: "column", gap: 1 }}>
        {eventDate && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <CalendarToday sx={{ fontSize: 18, color: "text.secondary" }} />
            <Typography variant="body2">{eventDate}</Typography>
          </Box>
        )}
        {eventTime && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <AccessTime sx={{ fontSize: 18, color: "text.secondary" }} />
            <Typography variant="body2">{eventTime}</Typography>
          </Box>
        )}
        {location && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <LocationOn sx={{ fontSize: 18, color: "text.secondary" }} />
            <Typography variant="body2">{location}</Typography>
          </Box>
        )}
      </Box>
    </Box>
  );

  const ClientSection = () => (
    <Box sx={{ mb: 2.5 }}>
      <Typography
        variant="caption"
        sx={{
          color: "text.secondary",
          textTransform: "uppercase",
          letterSpacing: 0.5,
          fontWeight: 600,
        }}
      >
        Client Information
      </Typography>
      <Box sx={{ mt: 1.5, display: "flex", flexDirection: "column", gap: 1 }}>
        {clientName && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Person sx={{ fontSize: 18, color: "text.secondary" }} />
            <Typography variant="body2">{clientName}</Typography>
          </Box>
        )}
        {clientPhone && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Phone sx={{ fontSize: 18, color: "text.secondary" }} />
            <Typography variant="body2">{clientPhone}</Typography>
          </Box>
        )}
      </Box>
    </Box>
  );

  const sections = [];
  let sectionCount = 0;

  if (hasBelovedInfo) {
    sections.push(<BelovedSection key="beloved" />);
    sectionCount++;

    if (hasEventInfo) {
      if (sectionCount > 0)
        sections.push(<Divider key="div1" sx={{ my: 2 }} />);
      sections.push(<EventSection key="event" />);
      sectionCount++;
    }

    if (hasClientInfo) {
      if (sectionCount > 0)
        sections.push(<Divider key="div2" sx={{ my: 2 }} />);
      sections.push(<ClientSection key="client" />);
      sectionCount++;
    }
  } else {
    if (hasEventInfo) {
      sections.push(<EventSection key="event" />);
      sectionCount++;
    }

    if (hasClientInfo) {
      if (sectionCount > 0)
        sections.push(<Divider key="div1" sx={{ my: 2 }} />);
      sections.push(<ClientSection key="client" />);
      sectionCount++;
    }
  }

  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: 350,
        bgcolor: "background.paper",
        borderRadius: 3,
        boxShadow: 2,
        overflow: "hidden",
      }}
    >
      <Box sx={{ bgcolor: colors.primary[400], color: "white", p: 1.5 }}>
        <Typography
          variant="h6"
          sx={{ p: 2, fontWeight: 600, fontSize: "1rem" }}
        >
          Quote Preview
        </Typography>
      </Box>

      <Box sx={{ px: 2.5, py: 2 }}>

        <FuneralPreviewCard
          deceasedName={belovedName || "Beloved Name"}
          birthDate=""
          deathDate=""
          serviceDate={eventDate}
          serviceTime={eventTime}
          timeZone="CAT"
          location={location || "Service Location"}
          isLiveStream
          broadcastBy="MUZUKURU"
          circleImageUrl={photoPreview || ""}
        />
        {sections.length > 0 ? (
          sections
        ) : (
          <Typography
            variant="body2"
            color="text.secondary"
            textAlign="center"
          ></Typography>
        )}
      </Box>
    </Box>
  );
};

export default QuotePreview;
